import React, { useRef, useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

interface OTPInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  onComplete?: (value: string) => void;
}

export function OTPInput({
  value,
  onChange,
  length = 6,
  disabled = false,
  error = false,
  className,
  onComplete,
}: OTPInputProps) {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (value.length === length && onComplete) {
      onComplete(value);
    }
  }, [value, length, onComplete]);

  const handleInputChange = (index: number, inputValue: string) => {
    const digit = inputValue.slice(-1); // Only take the last character
    if (!/^\d*$/.test(digit)) return; // Only allow digits

    const newValue = value.split('');
    newValue[index] = digit;
    const newOTP = newValue.join('');
    
    onChange(newOTP);

    // Auto-focus next input
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newValue = value.split('');
      
      if (newValue[index]) {
        // Clear current digit
        newValue[index] = '';
      } else if (index > 0) {
        // Move to previous input and clear it
        newValue[index - 1] = '';
        inputRefs.current[index - 1]?.focus();
      }
      
      onChange(newValue.join(''));
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, ''); // Remove non-digits
    const newValue = pastedData.slice(0, length);
    onChange(newValue);
    
    // Focus the next empty input or the last input
    const nextIndex = Math.min(newValue.length, length - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleFocus = (index: number) => {
    setFocusedIndex(index);
  };

  const handleBlur = () => {
    setFocusedIndex(null);
  };

  return (
    <div className={cn('flex gap-2 justify-center', className)}>
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={() => handleFocus(index)}
          onBlur={handleBlur}
          onPaste={handlePaste}
          disabled={disabled}
          className={cn(
            'w-12 h-12 text-center text-lg font-semibold border-2 rounded-md',
            'focus:outline-none focus:ring-2 focus:ring-primary transition-colors',
            'bg-background text-foreground',
            error
              ? 'border-red-500 focus:ring-red-500'
              : focusedIndex === index
              ? 'border-primary'
              : 'border-input',
            disabled && 'opacity-50 cursor-not-allowed',
            value[index] && !error && 'border-primary bg-primary/5'
          )}
          aria-label={`Digit ${index + 1}`}
        />
      ))}
    </div>
  );
}